// Deposit tracking service
import { getChainType } from '../utils/chainDetect';

/**
 * Normalise a user address for storage/lookup. EVM addresses are hex and
 * case-insensitive — we lowercase them so `0xAbC` and `0xabc` match. Tron
 * (Base58Check) and Solana (Base58) addresses ARE case-sensitive — uppercase
 * `T` in Tron is part of the address; lowercasing it produces an invalid
 * address that fails `getChainType()` and causes the dashboard to filter the
 * deposit out. Preserve case for those.
 */
const normaliseAddress = (addr) => {
  if (!addr || typeof addr !== 'string') return addr;
  return getChainType(addr) === 'evm' ? addr.toLowerCase() : addr;
};

// Namespace localStorage key by API URL to isolate deposits per deployment
// (prevents localhost data from contaminating testnet/production and vice versa)
function getStorageKey() {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081';
  // Create a short, stable suffix from the API URL
  const suffix = apiUrl.replace(/https?:\/\//, '').replace(/[^a-zA-Z0-9]/g, '_');
  return `treasury_deposits_${suffix}`;
}

const STORAGE_KEY = getStorageKey();
const LEGACY_STORAGE_KEY = 'treasury_deposits';

export class DepositTracker {
  constructor() {
    this.deposits = this.loadDeposits();
  }

  loadDeposits() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
      // One-time migration: if namespaced key doesn't exist, check legacy key
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        const deposits = JSON.parse(legacy);
        // Save under new namespaced key and clear legacy to avoid future confusion
        localStorage.setItem(STORAGE_KEY, legacy);
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        console.log(`[DepositTracker] Migrated ${deposits.length} deposits from legacy key to ${STORAGE_KEY}`);
        return deposits;
      }
      return [];
    } catch (error) {
      console.error('Error loading deposits:', error);
      return [];
    }
  }

  saveDeposits() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.deposits));
    } catch (error) {
      console.error('Error saving deposits:', error);
    }
  }

  addDeposit(txHash, amount, userAddress, chainId, metadata = {}) {
    const deposit = {
      id: `${txHash}-${Date.now()}`,
      txHash,
      amount: parseFloat(amount),
      // Normalise: lowercase EVM only. Tron/Solana base58 is case-sensitive.
      userAddress: normaliseAddress(userAddress),
      chainId,
      timestamp: Date.now(),
      status: 'pending', // pending, confirmed, keys_requested, keys_received, withdrawn
      blockNumber: null,
      keyRequestId: null,
      keyShares: null,
      metadata: metadata // Store token, tokenAddress, crossChain, targetChain, etc.
    };

    this.deposits.unshift(deposit); // Add to beginning
    this.saveDeposits();
    return deposit;
  }

  updateDepositStatus(txHash, status, additionalData = {}) {
    const deposit = this.deposits.find(d => d.txHash === txHash);
    if (deposit) {
      deposit.status = status;
      Object.assign(deposit, additionalData);
      this.saveDeposits();
    } else {
      console.warn(`[DepositTracker] updateDepositStatus: deposit not found for txHash=${txHash?.slice(0, 16)}...`);
    }
    return deposit;
  }

  // Ensure a deposit exists in the tracker; create it if missing.
  // Returns the existing or newly created deposit.
  ensureDeposit(txHash, userAddress, amount = 0, chainId = null, metadata = {}) {
    let deposit = this.deposits.find(d => d.txHash === txHash);
    if (!deposit) {
      console.log(`[DepositTracker] Creating missing deposit for txHash=${txHash?.slice(0, 16)}...`);
      deposit = this.addDeposit(txHash, amount, userAddress, chainId, metadata);
    }
    return deposit;
  }

  getUserDeposits(userAddress) {
    const target = normaliseAddress(userAddress);
    // One-time migration: earlier code lowercased Tron/Solana addresses
    // unconditionally. If the caller passes a Tron `T...` (case-sensitive) and
    // we find a deposit stored as its lowercased form, restamp it. Same logic
    // for Solana base58. EVM is unaffected because target is already lowercase.
    if (userAddress && target !== userAddress.toLowerCase()) {
      const lowerVariant = userAddress.toLowerCase();
      let migrated = false;
      for (const d of this.deposits) {
        if (d.userAddress === lowerVariant) {
          d.userAddress = target;
          migrated = true;
        }
      }
      if (migrated) {
        this.saveDeposits();
        console.log(`[DepositTracker] Migrated deposit(s) from lowercased ${lowerVariant} to canonical ${target}`);
      }
    }
    return this.deposits.filter(d =>
      d.userAddress === target
    ).sort((a, b) => b.timestamp - a.timestamp);
  }

  getDepositByTxHash(txHash) {
    return this.deposits.find(d => d.txHash === txHash);
  }

  // Check deposit confirmation on blockchain
  async checkDepositConfirmation(provider, txHash) {
    try {
      const receipt = await provider.getTransactionReceipt(txHash);
      if (receipt && receipt.status === 1) {
        this.updateDepositStatus(txHash, 'confirmed', {
          blockNumber: receipt.blockNumber,
          gasUsed: receipt.gasUsed.toString()
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking deposit confirmation:', error);
      return false;
    }
  }

  // Check Solana deposit confirmation on blockchain
  async checkSolanaDepositConfirmation(connection, txHash) {
    try {
      const transaction = await connection.getTransaction(txHash, {
        maxSupportedTransactionVersion: 0
      });

      if (transaction && transaction.meta && transaction.meta.err === null) {
        this.updateDepositStatus(txHash, 'confirmed', {
          blockNumber: transaction.slot,
          blockTime: transaction.blockTime
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking Solana deposit confirmation:', error);
      return false;
    }
  }

  // Check Tron deposit confirmation via TronGrid HTTP. Doesn't need a wallet —
  // just polls the public endpoint. Defaults to Nile testnet.
  async checkTronDepositConfirmation(txHash, rpcUrl = 'https://nile.trongrid.io') {
    try {
      const resp = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/gettransactioninfobyid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: txHash.replace(/^0x/, '') }),
      });
      if (!resp.ok) return false;
      const info = await resp.json();
      if (info && info.id && info.blockNumber && (!info.receipt || !info.receipt.result || info.receipt.result === 'SUCCESS')) {
        this.updateDepositStatus(txHash, 'confirmed', {
          blockNumber: info.blockNumber,
          blockTime: info.blockTimeStamp,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking Tron deposit confirmation:', error);
      return false;
    }
  }

}

export default new DepositTracker();
